<script lang="ts">
  // Breakthrough · Signature: a light, editorial layout of the new fitness signature, each
  // part before and after, with the percentage gain.
  import { MEDAL_COLORS, type ReportContext } from '../context'
  import { statsFor } from '../stats'
  import Badges from '../parts/Badges.svelte'
  import Brand from '../parts/Brand.svelte'
  import Fill from '../parts/Fill.svelte'
  import Frame from '../parts/Frame.svelte'
  import Medal from '../parts/Medal.svelte'
  import PowerChart from '../parts/PowerChart.svelte'
  import StatGrid from '../parts/StatGrid.svelte'
  import TitleBlock from '../parts/TitleBlock.svelte'

  export let ctx: ReportContext

  $: bt = ctx.data.breakthrough!
  $: medal = MEDAL_COLORS[bt.level]
  $: stats = statsFor(ctx, ['stat.xss', 'stat.difficulty', 'stat.time', 'stat.distance', 'stat.xep', 'stat.recovery'])

  $: rows = (() => {
    const b = bt.before
    const a = bt.after
    if (!b || !a) return []
    const pct = (after: number, before: number) => (before ? ((after - before) / before) * 100 : 0)
    return [
      { label: 'Threshold Power', short: 'Threshold', before: ctx.fmt.int(b.tp), after: ctx.fmt.int(a.tp), unit: 'W', pct: pct(a.tp, b.tp) },
      { label: 'High Intensity Energy', short: 'High Intensity', before: ctx.fmt.fixed(b.hie, 1), after: ctx.fmt.fixed(a.hie, 1), unit: 'kJ', pct: pct(a.hie, b.hie) },
      { label: 'Peak Power', short: 'Peak Power', before: ctx.fmt.int(b.pp), after: ctx.fmt.int(a.pp), unit: 'W', pct: pct(a.pp, b.pp) },
    ]
  })()
  $: wide = ctx.size === 'landscape'
  $: maxPct = Math.max(1, ...rows.map((r) => Math.abs(r.pct)))
</script>

<Frame {ctx} light shade="even" base="linear-gradient(170deg, #fbfaf6 0%, #efebe2 100%)" photoFilter="grayscale(1)">
  <div class="xr-layout">
    <div class="xr-top">
      <div class="xr-badge">
        <Medal level={bt.level} size={ctx.size === 'square' ? 60 : 74} glow={false} />
        <span style="color: {medal.dark};">{bt.level === 'near' ? 'Near' : medal.label} Breakthrough</span>
      </div>
      {#if !wide}<Brand light height={38} />{/if}
    </div>

    <div class="xr-head">
      <h2 class="xr-headline">New Fitness{#if ctx.size === 'square'}{' '}{:else}<br />{/if}Signature</h2>
      <TitleBlock {ctx} lines={1} />
    </div>

    {#if ctx.show('bars') && rows.length}
      <div class="xr-rows">
        {#each rows as row}
          <div class="xr-row">
            <div class="xr-row-head">
              <span class="xr-row-label">{ctx.size === 'square' && row.short ? row.short : row.label}</span>
              <span class="xr-row-pct" class:xr-down={row.pct < 0}>{row.pct >= 0 ? '+' : '−'}{Math.abs(row.pct).toFixed(1)}%</span>
            </div>
            <div class="xr-row-values">
              <span class="xr-before">{row.before}</span>
              <span class="xr-arrow">→</span>
              <span class="xr-after">{row.after}<small>{row.unit}</small></span>
            </div>
            <div class="xr-track"><div class="xr-bar" style="width: {Math.max(2, (Math.abs(row.pct) / maxPct) * 100)}%;" /></div>
          </div>
        {/each}
      </div>
    {/if}

    <div class="xr-chart">
      {#if ctx.show('chart') && ctx.data.series?.power}
        <Fill minHeight={110} let:width let:height>
          <PowerChart series={ctx.data.series} colored={ctx.options.colorPowerChart} showHr={ctx.options.chartHeartRate} hrColor="#a50013" {width} {height} breakthrough={bt.level} showMpa={ctx.show('mpa')} color="#1d2a30" mpaColor="var(--accent)" fillOpacity={0.22} />
        </Fill>
      {/if}
    </div>

    <div class="xr-stats"><StatGrid {stats} columns={wide ? 2 : 4} size="sm" /></div>
    <div class="xr-badges"><Badges {ctx} own="breakthrough" max={wide ? Infinity : 2} /></div>
    {#if wide}<div class="xr-brand"><Brand light height={38} /></div>{/if}
  </div>
</Frame>

<style>
  .xr-layout {
    flex: 1;
    min-height: 0;
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto auto auto minmax(96px, 1fr) auto auto;
    grid-template-areas: 'top' 'head' 'rows' 'chart' 'stats' 'badges';
    row-gap: 22px;
  }
  :global(.xr-square) .xr-layout {
    row-gap: 22px;
  }
  /* Landscape: the headline and numbers on the left, the signature rows and chart on the right. */
  :global(.xr-landscape) .xr-layout {
    grid-template-columns: 560px minmax(0, 1fr);
    grid-template-rows: auto auto minmax(0, 1fr) auto auto;
    grid-template-areas:
      'top rows'
      'head rows'
      '. chart'
      'stats chart'
      'badges brand';
    column-gap: 80px;
    row-gap: 26px;
  }
  .xr-top { grid-area: top; }
  .xr-head { grid-area: head; min-width: 0; }
  .xr-rows { grid-area: rows; }
  .xr-chart { grid-area: chart; display: flex; flex-direction: column; min-height: 0; }
  .xr-stats { grid-area: stats; }
  .xr-badges { grid-area: badges; }
  .xr-badges:empty { display: none; }
  .xr-brand { grid-area: brand; display: flex; justify-content: flex-end; align-items: flex-end; }
  .xr-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .xr-badge {
    display: flex;
    align-items: center;
    gap: 16px;
    font-size: 26px;
    font-weight: 800;
    letter-spacing: 0.16em;
    text-transform: uppercase;
  }
  .xr-headline {
    margin: 0 0 18px !important;
    font-family: var(--display);
    font-weight: 800;
    font-size: 132px;
    line-height: 0.88;
    text-transform: uppercase;
    letter-spacing: -0.005em;
  }
  :global(.xr-portrait) .xr-headline {
    font-size: 116px;
  }
  :global(.xr-portrait) .xr-after { font-size: 64px; }
  :global(.xr-portrait) .xr-before { font-size: 44px; }
  :global(.xr-square) .xr-headline {
    font-size: 96px;
    margin-bottom: 14px !important;
  }
  :global(.xr-landscape) .xr-headline {
    font-size: 124px;
  }
  .xr-rows {
    display: flex;
    flex-direction: column;
    gap: 24px;
    margin: 4px 0;
  }
  /* Square: the three parts side by side. */
  :global(.xr-square) .xr-rows {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 30px;
    margin: 6px 0;
  }
  :global(.xr-square) .xr-row-values {
    flex-wrap: wrap;
    column-gap: 10px;
  }
  :global(.xr-square) .xr-row-head {
    flex-direction: column;
    align-items: flex-start;
  }
  :global(.xr-square) .xr-before { font-size: 36px; }
  :global(.xr-square) .xr-arrow { font-size: 30px; }
  :global(.xr-square) .xr-after { font-size: 60px; }
  :global(.xr-square) .xr-row-pct { font-size: 38px; }
  :global(.xr-square) .xr-row-label { font-size: 20px; }
  :global(.xr-landscape) .xr-rows {
    gap: 34px;
    margin: 0;
  }
  .xr-row-head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
  }
  .xr-row-label {
    font-size: 24px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--muted);
  }
  .xr-row-pct {
    font-family: var(--display);
    font-weight: 700;
    font-size: 44px;
    color: var(--good);
  }
  .xr-row-pct.xr-down {
    color: var(--bad);
  }
  .xr-row-values {
    display: flex;
    align-items: baseline;
    gap: 16px;
    margin-top: -4px;
    font-family: var(--display);
    font-weight: 700;
    line-height: 1;
  }
  .xr-before {
    font-size: 48px;
    color: var(--muted);
  }
  .xr-arrow {
    font-size: 40px;
    color: var(--muted);
  }
  .xr-after {
    font-size: 72px;
  }
  .xr-after small {
    font-size: 30px;
    margin-left: 6px;
    color: var(--muted);
  }
  .xr-track {
    margin-top: 12px;
    height: 12px;
    border-radius: 999px;
    background: var(--faint);
    overflow: hidden;
  }
  .xr-bar {
    height: 100%;
    border-radius: 999px;
    background: var(--accent);
  }
</style>
